import type { Mesh, MeshStandardMaterial } from 'three';

import type { GeometryBuilder } from '../geometry-builder.class';
import type { AddPanelInfo } from '../../../../model/add-panel-info';
import { TextureType } from '../../../../model/enums';
import { loadCSG } from '../../../../utils/lazy-three-csg';
import { getActualQuality } from '../helpers';

/**
 * Check if CSG should be applied
 */
export const shouldApplyCsgCfn = (
	_self: GeometryBuilder,
	{ api, generateFromModel, model }: {
		api: AddPanelInfo;
		generateFromModel: boolean;
		model: Mesh | null;
	}
): boolean => {
	return (
		api.modelPath != null &&
		api.modelPath !== '' &&
		generateFromModel &&
		model != null &&
		api.panelType === TextureType.Surround
	);
};

/**
 * Apply CSG intersection for slat textures
 * CSG module is lazy-loaded on first use.
 */
export const applyCsgIntersectionCfn = async (
	self: GeometryBuilder,
	{ rawPanel, model, mat, api }: {
		rawPanel: Mesh;
		model: Mesh;
		mat: MeshStandardMaterial;
		api: AddPanelInfo;
	}
): Promise<Mesh> => {
	// Lazy load CSG module
	const CSG = await loadCSG();

	// Update matrices
	model.updateMatrix();
	rawPanel.updateMatrix();

	// Convert to CSG
	const modelCSG = CSG.fromMesh(model);
	const rawPanelCSG = CSG.fromMesh(rawPanel);

	// Perform intersection
	const resultCSG = rawPanelCSG.intersect(modelCSG);

	// Convert back to mesh
	const resultMesh = resultCSG.toMesh(model.matrix, mat) as Mesh;

	// Apply quality optimizations
	const actualQuality = getActualQuality(self.configAccess.renderQuality);
	const { geometry } = resultMesh;

	// Recompute normals for all quality levels
	geometry.deleteAttribute('normal');
	if (geometry.attributes.tangent) {
		geometry.deleteAttribute('tangent');
	}
	if (geometry.attributes.color) {
		geometry.deleteAttribute('color');
	}

	if (actualQuality === 0) {
		// Wireframe mode: no normals or UVs needed
		geometry.deleteAttribute('uv');
	} else {
		// Recompute normals - CSG preserves UVs from input meshes
		geometry.computeVertexNormals();
	}

	// Configure material
	if (resultMesh.material) {
		const meshMat = resultMesh.material as MeshStandardMaterial;
		meshMat.side = 0; // FrontSide
		meshMat.flatShading = true;
		meshMat.wireframe = self.configAccess.qualitySettings?.wireframe ?? false;

		if (actualQuality <= 3) {
			meshMat.normalMap = null;
			meshMat.roughnessMap = null;
			meshMat.metalnessMap = null;
			meshMat.aoMap = null;
			if (actualQuality === 0) {
				meshMat.map = null;
			}
		}
		meshMat.needsUpdate = true;
	}

	// Set userData
	resultMesh.userData = {
		id: api.id,
		type: 'panel',
		textureType: TextureType.Surround,
		islandIndex: api.islandIndex,
		hash: api.hash,
		csgModel: true
	};

	// Shadow settings
	const enableShadows = self.configAccess.qualitySettings?.shadowMapEnabled ?? false;
	resultMesh.castShadow = enableShadows;
	resultMesh.receiveShadow = enableShadows;

	// Performance optimizations
	resultMesh.matrixAutoUpdate = false;
	resultMesh.updateMatrix();

	if (actualQuality <= 2) {
		resultMesh.frustumCulled = false;
	}

	return resultMesh;
};
